import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, defer, of } from "rxjs";
import { catchError, exhaustMap, map, retry, switchMap, tap } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { AddressDbService } from "@app/address/store/services/address-db.service";
import { entityRoutePaths } from "@app/entity/entity.routes";
import { Entity } from "@app/entity/model/entity";
import { EntityDbService } from "@app/entity/services/entity-db.service";
import { DataActions } from "@app/project/store/actions";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { UserManagementActions } from "@app/user/store";
import { Action } from "@ngrx/store";
import { EntityActions } from "../actions";
import { createAuditLogKey, LOG_CATEGORY, LOG_TYPE, LOG_SUB_TYPE } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";

@Injectable()
export class EntityEffects extends EffectsUtils {
    listRoutePath = `${entityRoutePaths.root}${entityRoutePaths.list}`;

    constructor(
        actions$: Actions,
        private addressDbService: AddressDbService,
        private entityDbService: EntityDbService,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly auditLogService: AuditLogService
    ) {
        super(actions$);
    }

    generateAccountId() {
        let text = "CC-";
        const possible = "0123456789";
        for (let i = 0; i < 6; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length)); // NOSONAR
        }

        return text;
    }

    createEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.createSet.request),
            switchMap(({ entity, callbackEmitter }) =>
                defer(() => {
                    const payload = {
                        ...entity,
                        accountId: this.generateAccountId(),
                    };
                    return this.toAction(
                        this.entityDbService.createEntity(payload).pipe(map(entity => ({ entity }))),
                        EntityActions.createSet,
                        callbackEmitter
                    );
                }).pipe(
                    // Throw error and retry if accountId already exists
                    // @TODO: The any shouldn't be necessary here. Figure out why error type isn't on toAction()'s return type
                    // @TODO: Id generation should be handled by backend
                    tap(({ error }: any) => {
                        if (error?.includes(`duplicate key value violates unique constraint "entity_unique_account_id"`)) {
                            throw error;
                        }
                    }),
                    retry({
                        count: 2,
                        delay: error => {
                            if (error.includes(`duplicate key value violates unique constraint "entity_unique_account_id"`)) {
                                return of(null);
                            }
                            throw new DataServiceError(error);
                        },
                    }),
                    catchError(DataActions.toError2(EntityActions.createSet.failure))
                )
            )
        )
    );

    createEntitySuccess$ = this.createSuccessEffect(EntityActions.createSet.success, ActionWord.created);
    createEntityFailure$ = this.createFailureEffect(EntityActions.createSet.failure, ActionWord.created);

    getEntities$ = this.createSwitchEffect({
        dbMethod: ({ parentId }) => this.entityDbService.getEntities(parentId),
        actionSet: EntityActions.loadSet,
    });

    updateEntityAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.updateEntityAddress),
            exhaustMap(({ entity, address, addressType, callbackEmitter }) => {
                const update = {
                    [`${addressType}Address`]: address.id,
                };
                const _entity = Object.assign({ ...entity }, update);
                return of(EntityActions.updateSet.request({ entity: _entity, callbackEmitter }));
            }),
            catchError(DataActions.toError2(EntityActions.updateSet.failure))
        )
    );

    removeEntityAddress$ = this.createExhaustEffect({
        dbMethod: payload => this.addressDbService.deleteAddress(payload.address).pipe(map(_ => payload)),
        actionSet: EntityActions.removeEntityAddressSet,
    });

    removeEntityAddressSuccess$ = this.createSuccessEffect(EntityActions.removeEntityAddressSet.success, ActionWord.saved, "Entity");
    removeEntityAddressFailure$ = this.createFailureEffect(EntityActions.removeEntityAddressSet.failure, ActionWord.saved, "Entity");

    updateEntityUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityActions.updateEntityUserSet.request),
            exhaustMap(({ userType, user, entity, callbackEmitter }) => {
                const update = Entity.fromProps({ ...entity, [`${userType}Userinfo`]: user.id });
                callbackEmitter.emit();
                return [
                    UserManagementActions.UserActions.upsertSuccessSilent({ user }),
                    EntityActions.updateEntityUserSet.success({ entity: update }),
                ];
            }),
            catchError(DataActions.toError2(EntityActions.updateEntityUserSet.failure))
        )
    );

    updateEntity$ = this.createExhaustEffect({
        dbMethod: ({ entity }) => this.entityDbService.updateEntity(entity).pipe(map(() => ({ entity }))),
        actionSet: EntityActions.updateSet,
    });

    updateEntitySuccess$ = this.createSuccessEffect(EntityActions.updateSet.success, ActionWord.saved);
    updateEntityFailure$ = this.createFailureEffect(EntityActions.updateSet.failure, ActionWord.saved);

    deleteEntity$ = this.createExhaustEffect({
        dbMethod: ({ entity }) =>
            this.entityDbService.deleteEntity(entity).pipe(
                tap(_ => {
                    const logKey = createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.COMPANY, LOG_SUB_TYPE.DELETE);
                    const data = `Company Name: ${entity.name}, CC-Number: ${entity.accountId}, Scode: ${
                        entity.snaponeCode?.length ? entity.snaponeCode : "None"
                    }`;
                    this.auditLogService.logEvent(logKey, { data, entityId: entity.id });
                }),
                map(() => ({ entity }))
            ),
        actionSet: EntityActions.deleteSet,
    });

    deleteEntityConfirmation$ = this.createDeleteConfirmationEffect({
        actionSet: EntityActions.deleteConfirmationSet,
        dbMethod: ({ entity }) => of({ payload: { entity }, display: entity?.name || "this entity" }),
        confirmationService: this.deleteConfirmationService,
    });

    deleteEntitySuccess$ = this.createSuccessEffect(EntityActions.deleteSet.success, ActionWord.deleted, "Company", this.listRoutePath);
    deleteEntityFailure$ = this.createFailureEffect(EntityActions.deleteSet.failure, ActionWord.deleted);
}
